import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Link } from "react-router-dom";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { Autoplay, Navigation, Pagination } from "swiper/modules";

import { API } from "../../config";

interface CardAttribute {
  attribute_id?: number | null;
  attribute_name?: string | null;
  attribute_value?: string | null;
  display_order?: number | null;
}

interface HomeCard {
  home_course_id: number;
  course_category_id: number;
  category_name: string | null;
  category_slug: string | null;
  category_description: string | null;
  description: string | null;
  image_url: string | null;
  card_attributes: CardAttribute[];
  display_order: number | null;
  record_status_id: number;
}


function extractLearnItems(html: string): string[] {
  if (!html) return [];

  // Extract <li> items first
  const toPlain = (h: string) => {
    const d = document.createElement("div");
    d.innerHTML = h;
    return (d.textContent || d.innerText || "").replace(/\u00a0/g, " ").trim().replace(/-/g, "‑");
  };

  const liItems = Array.from(
    html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi),
    (m) => toPlain(m[1])
  ).filter(Boolean);
  if (liItems.length > 0) return liItems;

  const pItems = Array.from(
    html.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi),
    (m) => toPlain(m[1])
  ).filter(Boolean);
  if (pItems.length > 0) return pItems;

  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").replace(/-/g, "‑");
  return text.split(/\n/).map((l) => l.trim()).filter(Boolean);
}

export default function CourseSlider() {
  const [cards, setCards] = useState<HomeCard[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/home_course_cards`)
      .then((r) => r.json())
      .then((data) => {
        const active = ((data.cards ?? []) as HomeCard[])
          .filter((c) => c.record_status_id === 1)
          .sort((a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity));
        setCards(active);
      })
      .catch((err) => console.error("Failed to load courses:", err))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="courses" className="py-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="heading-bold headingcolor">Courses at <span className="whitespace-nowrap">Phonics Power</span></h2>
          <p className="subheading pt-4">
            From strong foundations to fluent communication — English courses for every stage of learning.
          </p>
        </div>
        <div className="flex justify-center md:justify-end mb-8">
          <Link to="/ViewAllCourse">
            <button className="w-64 md:w-auto border border-red-500 text-red-700 hover:bg-red-500 hover:text-white font-semibold px-6 py-2 rounded-lg transition duration-300">
              Find Courses
            </button>
          </Link>
        </div>

        {loaded && cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 mb-4" aria-hidden="true">
              <path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path>
            </svg>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No courses available at the moment</h3>
            <p className="text-sm text-gray-600">Please check back soon. New courses are being added regularly.</p>
          </div>
        ) : (
          <div className="wrapper px-55 relative">
            <Swiper
                  spaceBetween={30}
                  autoplay={{ delay: 5000, disableOnInteraction: false }}
                  pagination={{ el: ".course-pagination", clickable: true }}
                  navigation={{ nextEl: ".course-next", prevEl: ".course-prev" }}
                  modules={[Autoplay, Pagination, Navigation]}
                  className="mySwiper"
                  style={{ alignItems: "stretch" }}
                  slidesPerView={4}
                  loop={cards.length > 4}
                  watchOverflow={true}
                  breakpoints={{
                    320: { slidesPerView: 1, spaceBetween: 15 },
                    640: { slidesPerView: 1, spaceBetween: 20 },
                    768: { slidesPerView: 2, spaceBetween: 25 },
                    1024: { slidesPerView: 3, spaceBetween: 30 },
                    1280: { slidesPerView: 4, spaceBetween: 30 },
                  }}
                >
                  {cards.map((card) => {
                    const learnLines = extractLearnItems(card.description ?? "").slice(0, 4);

                    const ageAttr = card.card_attributes?.find((a) =>
                      a.attribute_name?.toLowerCase().includes("age")
                    );

                    const learnMore = card.category_name
                      ? `/ViewAllCourse?category=${encodeURIComponent(card.category_name)}`
                      : "/ViewAllCourse";

                    return (
                      <SwiperSlide key={card.home_course_id} style={{ height: "auto" }}>
                        <div className="w-full h-full min-w-0">
                          <div className="flex flex-col rounded-xl hover:shadow-xl transition-shadow duration-300 shadow-md h-full bg-white overflow-hidden w-full">

                            {/* Image */}
                            <div className="relative flex-shrink-0">
                              <img
                                src={card.image_url || "/images/phonics.webp"}
                                alt={card.category_name ? `${card.category_name} course` : "Phonics Power course"}
                                className="w-full h-40 md:h-48 object-cover rounded-t-xl"
                                width={400}
                                height={300}
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.src = "/images/phonics.webp"; }}
                              />
                              {ageAttr?.attribute_value && (
                                <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium absolute top-4 left-4 bg-green-900 text-white border-transparent">
                                  {ageAttr.attribute_value}
                                </span>
                              )}
                            </div>

                            {/* Card body */}
                            <div className="flex flex-col flex-1 px-3 pt-3 pb-3 gap-2 md:px-4 md:pt-4 md:pb-4 md:gap-3 min-w-0 w-full">

                              {/* Title */}
                              <h3 className="cou-card-head">{card.category_name}</h3>
                              {(() => {
                                const name = (card.category_name || "").toLowerCase();
                                let tagline = "";
                                if (name.includes("phonics")) tagline = "Build strong reading skills with systematic phonics learning";
                                else if (name.includes("grammar") || name.includes("writing")) tagline = "Grow grammar skills progressively and strengthen your writing";
                                else if (name.includes("cambridge")) tagline = "Prepare for Cambridge English exams with expert guidance";
                                else if (name.includes("spoken") || name.includes("adult")) tagline = "English for Adults - For Life and Work";
                                else if (name.includes("training")) tagline = "Professional Development for Educators";
                                return tagline ? (
                                  <p className="cou-card-subhead mt-1 w-full" style={{ wordBreak: "normal", overflowWrap: "break-word", hyphens: "none" }}>
                                    {tagline}
                                  </p>
                                ) : null;
                              })()}

                              {/* Attribute rows — skip Age (shown as badge) */}
                              {card.card_attributes?.filter((a) => (a.attribute_name || a.attribute_value) && !a.attribute_name?.toLowerCase().includes("age")).map((a, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                  <span className="cou-card-subhead">{a.attribute_name ?? ""}:</span>
                                  <span className="cou-card-subhead">{a.attribute_value ?? ""}</span>
                                </div>
                              ))}

                              {/* Description / learn items */}
                              {learnLines.length > 0 && (
                                <div className="space-y-1">
                                  <p className="cou-card-subhead cou-card-subhead-bold">What you'll learn:</p>
                                  <ul className="space-y-1">
                                    {learnLines.map((item, i) => (
                                      <li key={i} className="flex items-start min-w-0 w-full">
                                        <span className="cou-card-subhead tick-color mr-2 flex-shrink-0">✓</span>
                                        <span className="cou-card-subhead flex-1 min-w-0" style={{ wordBreak: "normal", overflowWrap: "break-word", hyphens: "none" }}>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Learn More */}
                              <div className="border-gray-200 border-t mt-auto pt-3 text-center">
                                <Link to={learnMore}>
                                  <button className="course-btn hover:bg-red-700 text-white">
                                    Learn More
                                    <span className="sr-only">{card.category_name ? ` about ${card.category_name}` : ""}</span>
                                  </button>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
            <div className="course-prev"><b>❮</b></div>
            <div className="course-next"><b>❯</b></div>
            <div className="course-pagination mt-4 text-center"></div>
          </div>
        )}
      </div>
    </section>
  );
}
